import { useMutation } from '@apollo/client';
import { GET_COMMUNITY_GROUPS } from '@communities/community-groups/graphql';
import { DELETE_COMMUNITY_GROUP } from '@communities/community-groups/graphql/deleteCommunityGroup';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface DeleteCommunityGroupResponse {
  deleteCommunityGroup: {
    success: boolean;
    message?: string;
  };
}

export function useDeleteCommunityGroup() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [deleteCommunityGroupMutation, { loading }] = useMutation<DeleteCommunityGroupResponse>(DELETE_COMMUNITY_GROUP, {
    refetchQueries: [{ query: GET_COMMUNITY_GROUPS }],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const deleteCommunityGroup = async (communityGroupId: string): Promise<boolean> => {
    try {
      track('DeleteCommunityGroupInitiated', {
        communityGroupId,
      });

      await deleteCommunityGroupMutation({
        variables: { id: communityGroupId },
      });

      track('DeleteCommunityGroupSucceeded', {
        communityGroupId,
      });

      return true;
    } catch (error) {
      track('DeleteCommunityGroupFailed', {
        communityGroupId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    deleteCommunityGroup,
    loading,
    errors,
    resetErrors,
  };
}

export default useDeleteCommunityGroup;
